import { Info } from 'lucide-react';
import { useUIStore } from '@/stores';
import type { PageKey, GroupBy } from '@/stores/uiStore';

interface GroupByDropdownProps {
  pageKey: PageKey;
  showInfoTooltip?: boolean;
  includeOwner?: boolean;
  excludeOptions?: GroupBy[];  // Options to hide (e.g., ['date'] for All Recurring tab)
}

const baseGroupByOptions: { value: GroupBy; label: string }[] = [
  { value: 'date', label: 'Date' },
  { value: 'category', label: 'Category' },
  { value: 'itemGroup', label: 'Item Group' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
];

const ownerOption: { value: GroupBy; label: string } = { value: 'owner', label: 'Owner' };

const selectClassName = "rounded-lg border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500";

export function GroupByDropdown({ pageKey, showInfoTooltip = false, includeOwner = false, excludeOptions = [] }: GroupByDropdownProps) {
  let groupByOptions = includeOwner ? [ownerOption, ...baseGroupByOptions] : baseGroupByOptions;

  // Filter out excluded options
  if (excludeOptions.length > 0) {
    groupByOptions = groupByOptions.filter(opt => !excludeOptions.includes(opt.value));
  }
  const { groupBy, setGroupBy } = useUIStore();
  const currentGroupBy = groupBy?.[pageKey] ?? 'date';

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-gray-700">Group by:</label>
      <select
        value={currentGroupBy}
        onChange={(e) => setGroupBy(pageKey, e.target.value as GroupBy)}
        className={selectClassName}
      >
        {groupByOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {showInfoTooltip && currentGroupBy === 'date' && (
        <div className="relative group">
          <Info className="h-4 w-4 text-gray-400" />
          <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 right-0">
            <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
              Due Date if available, else Created Date
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
